import React from 'react';
import styled from 'styled-components';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useTranslator from 'Client/hooks/useTranslator';
import paths from 'Common/routes/paths';
import { useBellaDispatch, useBellaSelector } from 'Client/redux/hooks';
import { hideOverlay } from 'Client/redux/header/overlays/actions';
import { OverlayId } from 'Client/components/Overlays/types';
import ActiveOverlayPointerWrapper from 'Client/components/Overlays/ContentOverlay/ActiveOverlayPointerWrapper';
import Menu from 'Client/components/Overlays/Menu';
import { MenuItemType } from 'Client/components/Overlays/Menu/types';
import ContentfulImage from 'Client/components/Generic/ContentfulImage';
import { EventDispatcher } from 'Common/events';
import useGa from 'Client/hooks/useGa';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import HybridLink from 'Client/components/Generic/Links/HybridLink';
import { ensureEndingSlash } from 'Client/urlService';

const IdsBannerImage = styled(HybridLink)`
    display: flex;
    flex-direction: column;
    margin: 16px 16px -16px 16px;
`;

const IdsCtaContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 20px 15px 20px;
    position: relative;
    ::before {
        content: '';
        position: absolute;
        top: -15px;
        left: 0;
        right: 0;
        height: 1px;
        background-color: ${props => props.theme.colors.charcoal20};
    }
`;

const IDSCta = styled(ActionButton).attrs({
    kind: 'primary',
})`
    && {
        background: ${props => props.theme.colors.charcoal100};
    }
    &&:hover {
        background: ${props => props.theme.colors.charcoal80};
    }
`;

export const IdsDropdownMenu = () => {
    const ga = useGa();
    const t = useTranslator();
    const push = useBellaPush();
    const dispatch = useBellaDispatch();
    const idsDropdown = useBellaSelector(state => state.header.idsDropdown);

    const handleOverlayClose = () => dispatch(hideOverlay(OverlayId.IDS_DROPDOWN));

    const menuSections = React.useMemo(() => {
        const menuItems = [
            {
                title: t('IDS Drop-down - Your IDS'),
                url: ensureEndingSlash(paths.INTERIOR_DESIGN_SERVICE),
                itemType: MenuItemType.InternalLink,
            },
            {
                title: t('IDS Drop-down - Projects'),
                url: `${paths.INTERIOR_DESIGN_SERVICE}/#our-projects`,
                itemType: MenuItemType.InternalLink,
            },
            {
                title: t('IDS Drop-down - Reviews'),
                url: `${paths.INTERIOR_DESIGN_SERVICE}/#reviews`,
                itemType: MenuItemType.InternalLink,
            },
        ];

        const sections = [
            {
                key: 'idsMenu',
                title: '',
                items: menuItems,
            },
        ];
        return sections;
    }, [t]);

    const handleCTAClick: React.MouseEventHandler<HTMLButtonElement> = () => {
        push(`${paths.INTERIOR_DESIGN_SERVICE}/wizard/project`);
        setTimeout(() => {
            const eventDispatcher = EventDispatcher();
            eventDispatcher.startIdsFlow();
        }, 100);
    };

    const handleIdsMenuInteraction: React.MouseEventHandler<HTMLDivElement> = () => {
        ga.trackGenericEvent('Header', {
            action: 'Interior Design Icon',
            label: 'IDS flyout',
        });
    };

    return (
        <ActiveOverlayPointerWrapper onClick={handleIdsMenuInteraction}>
            {idsDropdown?.image && (
                <IdsBannerImage to={paths.INTERIOR_DESIGN_SERVICE}>
                    <ContentfulImage src={idsDropdown.image.file.url} alt={idsDropdown.image.title} />
                </IdsBannerImage>
            )}
            <Menu menuSections={menuSections} closeMenuFn={handleOverlayClose} />
            <IdsCtaContainer>
                <IDSCta onClick={handleCTAClick}>{t('IDS Drop-down - CTA')}</IDSCta>
            </IdsCtaContainer>
        </ActiveOverlayPointerWrapper>
    );
};
